/**
 * Retrieves the locale and date format based on the user's current time zone.
 *
 * This function uses the `Intl.DateTimeFormat().resolvedOptions().timeZone` to determine the user's current time zone.
 * It then maps this time zone to a predefined list of locales and date formats.
 *
 * @returns {Object} An object containing the `timeZone`, `locale`, and `dateFormat` for the user's current time zone.
 * If the time zone is not found in the predefined list, it returns `undefined`.
 *
 * Example usage:
 * ```javascript
 * const localeInfo = getLocaleAndFormatDateFromTimeZone();
 * console.log(localeInfo);
 * ```
 */
export function getLocaleAndFormatDateFromTimeZone() {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const map = [
    { timeZone: 'Pacific/Midway', locale: 'en-US', dateFormat: 'MM/DD/YYYY' },
    { timeZone: 'Pacific/Honolulu', locale: 'en-US', dateFormat: 'MM/DD/YYYY' },
    { timeZone: 'America/Juneau', locale: 'en-US', dateFormat: 'MM/DD/YYYY' },
    { timeZone: 'America/Los_Angeles', locale: 'en-US', dateFormat: 'MM/DD/YYYY' },
    { timeZone: 'America/Denver', locale: 'en-US', dateFormat: 'MM/DD/YYYY' },
    { timeZone: 'America/Chicago', locale: 'en-US', dateFormat: 'MM/DD/YYYY' },
    { timeZone: 'America/New_York', locale: 'en-US', dateFormat: 'MM/DD/YYYY' },
    { timeZone: 'America/Caracas', locale: 'es-VE', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'America/Sao_Paulo', locale: 'pt-BR', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Atlantic/Reykjavik', locale: 'is-IS', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Europe/London', locale: 'en-GB', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Europe/Berlin', locale: 'de-DE', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Europe/Moscow', locale: 'ru-RU', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Asia/Dubai', locale: 'ar-AE', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Asia/Karachi', locale: 'ur-PK', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Asia/Kolkata', locale: 'hi-IN', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Asia/Bangkok', locale: 'th-TH', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Asia/Shanghai', locale: 'zh-CN', dateFormat: 'YYYY/MM/DD' },
    { timeZone: 'Asia/Tokyo', locale: 'ja-JP', dateFormat: 'YYYY/MM/DD' },
    { timeZone: 'Australia/Sydney', locale: 'en-AU', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Pacific/Auckland', locale: 'en-NZ', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Africa/Cairo', locale: 'ar-EG', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Africa/Johannesburg', locale: 'en-ZA', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'America/Mexico_City', locale: 'es-MX', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'America/Toronto', locale: 'en-CA', dateFormat: 'MM/DD/YYYY' },
    { timeZone: 'Europe/Paris', locale: 'fr-FR', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Europe/Madrid', locale: 'es-ES', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Europe/Rome', locale: 'it-IT', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Asia/Singapore', locale: 'en-SG', dateFormat: 'DD/MM/YYYY' },
    { timeZone: 'Asia/Seoul', locale: 'ko-KR', dateFormat: 'YYYY/MM/DD' },
  ];

  return {
    ...(map[map.findIndex(item => item.timeZone === timeZone)] || {
      locale: 'it-IT',
      dateFormat: 'DD/MM/YYYY',
      timeZone: 'Europe/Rome',
    }),
    formatDatePicker: 'DD/MM/YYYY',
  };
}
